import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import jwtDecode from 'jwt-decode';

import oktaAuth from '../../../src/utils/auth/okta-auth';
import getAppConfig from '../../../src/utils/config/get-app-config';
import { appConfiguration } from '../../../src/utils/config/restore-original-uri';
import { EMAIL_REGEX } from '../../../src/utils/hooksLib';
import type { AddLoggerContextFn, DatadogConfig, JWTToken, LogConfig, LogContext, Logger as LoggerInterface, LogHandler, LogLevel, ParsedContext} from './types';

/**
 * Supported log levels
 */
const supportedLogLevels: readonly LogLevel[] = ['debug', 'info', 'warn', 'error'] as const;

/**
 * Supported log handler
 */
const supportedLogHandler: readonly LogHandler[] = ['http', 'console', 'silent'] as const;

/**
 * Default logger configuration
 */
const defaultLogConfig: LogConfig = {
  level: 'error',
  handler: 'http'
} as const;

const getDatadogConfigFromApp = (): DatadogConfig => {
  const config = appConfiguration('datadogConfig') || undefined;
  return typeof config === 'object' ? (config as DatadogConfig) : {};
};

const setLogLevel = (): void => {
  const config = appConfiguration('log') || undefined;
  let level = defaultLogConfig.level;
  if (
    config &&
    typeof config === 'object' &&
    'level' in config &&
    (config as { level?: string }).level &&
    supportedLogLevels.includes((config as { level: string }).level as LogLevel)
  ) {
    level = (config as { level: LogLevel }).level;
  }
  (datadogLogs.logger as unknown as { setLevel: (level: string) => void }).setLevel(level);
};

const setLogHandler = (): void => {
  const config = appConfiguration('log') || undefined;
  let handler = defaultLogConfig.handler;
  if (
    config &&
    typeof config === 'object' &&
    'handler' in config &&
    (config as { handler?: string }).handler &&
    supportedLogHandler.includes((config as { handler: string }).handler as LogHandler)
  ) {
    handler = (config as { handler: LogHandler }).handler;
  }
  (datadogLogs.logger as unknown as { setHandler: (handler: unknown) => void }).setHandler(handler);
};

const setDefaultContext = async (): Promise<void> => {
  const {
    oktaOIDC: { clientId }
  } = (getAppConfig as unknown as (key: string) => { oktaOIDC: { clientId: string } })('oktaOIDC');
  let {
    env: { env }
  } = (getAppConfig as unknown as (key: string) => { env: { env: string } })('env');
  datadogLogs.setLoggerGlobalContext({
    clientId,
    env,
    date: new Date() as unknown as string
  });
  datadogRum.setRumGlobalContext({
    clientId,
    env,
    date: new Date() as unknown as string
  });
};

const setGlobalContext = async (): Promise<void> => {
  let token: JWTToken = {};
  const jwtToken = await oktaAuth.getAccessToken();
  token = jwtToken ? jwtDecode<JWTToken>(jwtToken) : {};

  const userId = token.uid || undefined;
  const sessionId = token.jti || undefined;

  datadogLogs.addLoggerGlobalContext('user', userId);
  datadogLogs.addLoggerGlobalContext('session_id', sessionId);
  datadogRum.addRumGlobalContext('user', userId);
  datadogRum.addRumGlobalContext('session_id', sessionId);
};

const maskEmail = (data: string = ''): string => {
  let updatedData = data;
  const hasEmail = EMAIL_REGEX.test(data);
  if (hasEmail) {
    updatedData = data.replace(EMAIL_REGEX, '***emailId***');
    return maskEmail(updatedData);
  }
  return updatedData;
};

const parseContext = (context?: LogContext | string): ParsedContext => {
  let log_context: string | undefined;
  let response_time: number | undefined;
  if (typeof context === 'object') {
    try {
      log_context = context && context.logContext ? JSON.stringify(context.logContext) : JSON.stringify(context);
    } catch (error) {
      logger.error('Error stringifying log context', { error });
    }
    response_time = context && context.responseTime ? context.responseTime : undefined;
  } else {
    log_context = context;
  }
  return { log_context: maskEmail(log_context as string), response_time };
};

const addLoggerContext: AddLoggerContextFn = (key: string, value: unknown) => datadogLogs.logger.addContext(key, value as string | number | boolean);

const logger: LoggerInterface = {
  debug: (message: string, context?: LogContext | string) => datadogLogs.logger.debug(maskEmail(message), parseContext(context)),
  info: (message: string, context?: LogContext | string) => datadogLogs.logger.info(maskEmail(message), parseContext(context)),
  warn: (message: string, context?: LogContext | string) => datadogLogs.logger.warn(maskEmail(message), parseContext(context)),
  error: (message: string, context?: LogContext | string) => datadogLogs.logger.error(maskEmail(message), parseContext(context)),
  addLoggerContext: (key: string, value: unknown) => datadogLogs.logger.addContext(key, value as string | number | boolean)
};

const datadogConfigFromApp = getDatadogConfigFromApp();

datadogLogs.init({
  // ...defaultDatadogConfig,
  ...datadogConfigFromApp
} as unknown as Parameters<typeof datadogLogs.init>[0]);
datadogRum.init({
  // ...defaultDatadogConfig,
  ...datadogConfigFromApp
} as unknown as Parameters<typeof datadogRum.init>[0]);
setLogLevel();
setLogHandler();
setDefaultContext();

export { addLoggerContext, datadogLogs, logger, setGlobalContext };
